import { dahliaImages, flowerFlashImages, thingsWeLike, type Feature } from "@/mocks/home";
import { useReveal } from "@/hooks/useReveal";

function FeatureRow({ feature }: { feature: Feature }) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const imageLeft = feature.layout === "image-left";

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center`}
    >
      <div className={`w-full ${imageLeft ? "lg:order-1" : "lg:order-2"}`}>
        <div className="w-full h-[260px] md:h-[340px] overflow-hidden rounded-lg">
          <img
            src={feature.image}
            alt={feature.imageAlt || feature.title}
            title={feature.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
      <div className={imageLeft ? "lg:order-2" : "lg:order-1"}>
        <h3 className="font-heading text-2xl md:text-[28px] text-foreground-950 leading-snug">{feature.title}</h3>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground-700">{feature.body}</p>
        {feature.href && (
          <a
            href={feature.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            {feature.hrefLabel || "Learn more"}
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  );
}

function Gallery({ images, alt }: { images: string[]; alt: string }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      {images.map((src, i) => (
        <div key={src} className="w-full aspect-square overflow-hidden rounded-lg">
          <img
            src={src}
            alt={`${alt} ${i + 1}`}
            title={alt}
            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
          />
        </div>
      ))}
    </div>
  );
}

export default function ThingsWeLike() {
  const head = useReveal<HTMLDivElement>();
  const flash = useReveal<HTMLDivElement>();
  const dahlia = useReveal<HTMLDivElement>();

  return (
    <section id="things-we-like" className="w-full bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <div ref={head.ref} className={`reveal ${head.visible ? "is-visible" : ""} max-w-3xl mx-auto text-center`}>
          <p className="text-[11px] uppercase tracking-[0.32em] text-accent-600">Inspiration &amp; Community</p>
          <h2 className="mt-4 font-heading font-light text-3xl md:text-5xl text-foreground-950">Things We Like</h2>
          <p className="mt-6 text-[15px] leading-relaxed text-foreground-700">
            A curated collection of gardens, designers, artists and organizations that inspire our members &mdash;
            from floral artistry to the wild landscapes we work to protect.
          </p>
        </div>

        {/* Burlingame Flower Flash */}
        <div ref={flash.ref} className={`reveal ${flash.visible ? "is-visible" : ""} max-w-6xl mx-auto mt-16 md:mt-20`}>
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-accent-400" />
            <h3 className="font-heading text-xl md:text-2xl text-foreground-950">
              Community Outreach &mdash; Burlingame Flower Flash!
            </h3>
          </div>
          <div className="mt-6">
            <Gallery images={flowerFlashImages} alt="Burlingame Flower Flash" />
          </div>
        </div>

        {/* Dahlia Society */}
        <div ref={dahlia.ref} className={`reveal ${dahlia.visible ? "is-visible" : ""} max-w-6xl mx-auto mt-16 md:mt-20`}>
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-accent-400" />
            <h3 className="font-heading text-xl md:text-2xl text-foreground-950">Dahlia Society of California Annual Show</h3>
          </div>
          <div className="mt-6">
            <Gallery images={dahliaImages} alt="Dahlia Society show" />
          </div>
        </div>

        {/* Travel feature */}
        <div className="max-w-6xl mx-auto mt-16 md:mt-20 rounded-lg bg-secondary-100/70 border border-secondary-200 p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            <div>
              <h3 className="font-heading text-xl md:text-2xl text-secondary-950">Traveling? Don&rsquo;t Miss These Gardens</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-secondary-900/90">
                From estate gardens to public parks, our members have gathered a list of destinations worth a
                detour for any plant lover. Explore the gardens that continue to shape our club&rsquo;s vision.
              </p>
              <a
                href="https://gggp.org"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
              >
                View the gardens list
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </a>
            </div>
            <div className="w-full h-[220px] md:h-[260px] overflow-hidden rounded-lg">
              <img
                src="https://images.squarespace-cdn.com/content/v1/55db92c4e4b035628e290c2f/1499311948024-CFJTHNI595SYKLQ5MWS8/joseph-barrientos-17377.jpg?format=1500w"
                alt="Garden landscape to travel to"
                title="Traveling gardens"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Feature list */}
        <div className="max-w-6xl mx-auto mt-16 md:mt-24 space-y-16 md:space-y-24">
          {thingsWeLike.map((feature) => (
            <FeatureRow key={feature.title} feature={feature} />
          ))}
        </div>
      </div>
    </section>
  );
}